import { computed, nextTick } from "vue";
import { z } from "zod";

/** Background task data is serializable and belongs to one tenant and user. */
export type BackgroundTask = {
  id: string;
  kind: string;
  scope: string;
  label: string;
  status: "queued" | "running" | "ready" | "failed" | "cancelled";
  processed?: number;
  total?: number;
  message?: string;
  retryable?: boolean;
  restored?: boolean;
  /** A ready task that still needs the user, e.g. its download failed: the tray does not auto-remove it. */
  keep?: boolean;
};

const count = z.number().int().nonnegative().safe();
const backgroundTaskRow = z.object({
  id: z.string().trim().min(1), type: z.string().trim().min(1),
  status: z.enum(["queued", "running"]), processed: count, total: count,
});

/** Active server tasks supplied by the existing notifications response. */
export type BackgroundTaskRow = z.infer<typeof backgroundTaskRow>;

type TaskKindHandlers = Partial<Record<"cancel" | "download" | "retry", (task: BackgroundTask) => unknown>>;

/** Action handlers stay outside the Nuxt payload; register them on the client. */
export const taskKindHandlers = new Map<string, TaskKindHandlers>();

/** Register the actions owned by a background task kind. */
export function registerTaskKind(kind: string, handlers: TaskKindHandlers) {
  taskKindHandlers.set(kind, handlers);
  return () => {
    if (taskKindHandlers.get(kind) === handlers) taskKindHandlers.delete(kind);
  };
}

/** Shared background tasks, filtered to the current tenant and user. */
export function useBackgroundTasks() {
  const route = useRoute();
  const user = useSanctumUser<{ id: number | string }>();
  const scope = computed(() => `${route.params.tenant ?? ""}:${user.value?.id ?? ""}`);
  const state = useState<BackgroundTask[]>("background-tasks", () => []);
  const snapshot = useState<{ scope: string; rows: BackgroundTaskRow[]; invalid?: boolean }>("background-task-snapshot", () => ({ scope: "", rows: [] }));
  const activeTasks = computed(() => snapshot.value.scope === scope.value ? snapshot.value.rows : []);
  const invalidSnapshot = computed(() => snapshot.value.scope === scope.value && Boolean(snapshot.value.invalid));
  function restoreTasks(rows: unknown = [], taskScope = scope.value) {
    if (taskScope !== scope.value) return;
    const result = z.array(backgroundTaskRow).safeParse(rows);
    snapshot.value = result.success ? { scope: taskScope, rows: result.data } : { scope: taskScope, rows: [], invalid: true };
  }
  const compact = useState("background-tasks-compact", () => false);
  const bellOpen = useState("background-tasks-bell-open", () => false);
  const tasks = computed(() => state.value.filter(task => task.scope === scope.value));

  function upsertTask(task: BackgroundTask) {
    const index = state.value.findIndex(item => item.id === task.id && item.scope === task.scope);
    if (index === -1) state.value.push(task);
    else state.value[index] = task;
  }

  function removeTask(id: string, taskScope = scope.value) {
    state.value = state.value.filter(task => task.id !== id || task.scope !== taskScope);
  }

  async function dismissTask(task: BackgroundTask, returnFocus: boolean, focusTarget: () => HTMLElement | undefined) {
    if (!tasks.value.some(item => item.id === task.id && item.scope === task.scope)) return;
    removeTask(task.id, task.scope);
    await nextTick();
    if (returnFocus) focusTarget()?.focus();
  }

  return { scope, tasks, activeTasks, invalidSnapshot, restoreTasks, upsertTask, removeTask, dismissTask, compact, bellOpen };
}
